import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Plus, Search, Filter, MoreVertical, Eye, Edit2, Trash2,
  Building, Shield, Globe, Target, TrendingUp, AlertTriangle,
  Calendar, ChevronRight, BarChart3, PieChart, Activity
} from 'lucide-react';
import apiConfig from '../config/apiConfig';

const PortfolioGroupsManager = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetails, setGroupDetails] = useState(null);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    description: '',
    groupType: 'portfolio',
    riskThreshold: 'medium',
    industryFocus: '',
    maxCompanies: 50
  });

  useEffect(() => {
    fetchPortfolioGroups();
  }, []);

  const fetchPortfolioGroups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch portfolio groups: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setGroups(data.groups || []);
      } else {
        throw new Error(data.error || 'Failed to fetch portfolio groups');
      }

    } catch (err) {
      console.error('❌ Error fetching portfolio groups:', err);
      setError(err.message);
      
      if (err.message.includes('Authentication') || err.message.includes('401')) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    
    if (!createFormData.name.trim()) {
      alert('Group name is required');
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: createFormData.name,
          description: createFormData.description,
          groupType: createFormData.groupType,
          riskThreshold: createFormData.riskThreshold,
          industryFocus: createFormData.industryFocus,
          maxCompanies: parseInt(createFormData.maxCompanies)
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setShowCreateModal(false);
        setCreateFormData({
          name: '',
          description: '',
          groupType: 'portfolio',
          riskThreshold: 'medium',
          industryFocus: '',
          maxCompanies: 50
        });
        await fetchPortfolioGroups(); // Refresh the list
        alert('Portfolio group created successfully!');
      } else {
        throw new Error(data.error || 'Failed to create group');
      }

    } catch (err) {
      console.error('❌ Error creating group:', err);
      alert(`Failed to create group: ${err.message}`);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups/${groupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch group details: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setGroupDetails(data);
      } else {
        throw new Error(data.error || 'Failed to fetch group details');
      }

    } catch (err) {
      console.error('❌ Error fetching group details:', err);
      alert(`Failed to fetch group details: ${err.message}`);
    }
  };

  const fetchAvailableCompanies = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolio/companies`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch available companies: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setAvailableCompanies(data.companies || []);
      } else {
        throw new Error(data.error || 'Failed to fetch available companies');
      }

    } catch (err) {
      console.error('❌ Error fetching available companies:', err);
      // Don't show error for this as it might be expected
    }
  };

  const handleManageGroup = async (group) => {
    console.log('🎯 handleManageGroup called with group:', group);
    try {
      setSelectedGroup(group);
      setShowManageModal(true);
      console.log('✅ Modal state set, fetching data...');
      await Promise.all([
        fetchGroupDetails(group.group_id),
        fetchAvailableCompanies()
      ]);
      console.log('✅ Group details and companies loaded');
    } catch (error) {
      console.error('❌ Error in handleManageGroup:', error);
    }
  };

  const addCompaniesToGroup = async () => {
    if (selectedCompanies.length === 0) {
      alert('Please select at least one company to add');
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups/${selectedGroup.group_id}/companies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          companyIds: selectedCompanies
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`Successfully added ${data.addedCompanies.length} companies to the group`);
        setSelectedCompanies([]);
        await fetchGroupDetails(selectedGroup.group_id);
        await fetchPortfolioGroups(); // Refresh the main list
      } else {
        throw new Error(data.error || 'Failed to add companies');
      }

    } catch (err) {
      console.error('❌ Error adding companies to group:', err);
      alert(`Failed to add companies: ${err.message}`);
    }
  };

  const removeCompanyFromGroup = async (companyId) => {
    if (!confirm('Are you sure you want to remove this company from the group?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('foundations-token');
      
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/portfolioGroups/${selectedGroup.group_id}/companies/${companyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Company removed from group successfully');
        await fetchGroupDetails(selectedGroup.group_id);
        await fetchPortfolioGroups(); // Refresh the main list
      } else {
        throw new Error(data.error || 'Failed to remove company');
      }

    } catch (err) {
      console.error('❌ Error removing company from group:', err);
      alert(`Failed to remove company: ${err.message}`);
    }
  };

  const getGroupTypeIcon = (type) => {
    switch (type) {
      case 'captive': return Shield;
      case 'mssp': return Users;
      case 'custom': return Target;
      default: return Building;
    }
  };

  const getGroupTypeColor = (type) => {
    switch (type) {
      case 'captive': return 'text-green-600';
      case 'mssp': return 'text-purple-600';
      case 'custom': return 'text-orange-600';
      default: return 'text-blue-600';
    }
  };

  const getRiskThresholdColor = (threshold) => {
    switch (threshold?.toLowerCase()) {
      case 'high': return 'bg-red-100 text-[#ccd6f6]';
      case 'medium': return 'bg-yellow-100 text-[#ccd6f6]';
      case 'low': return 'bg-green-100 text-[#ccd6f6]';
      default: return 'bg-[#172a45] text-[#e6f1ff]';
    }
  };

  const filteredGroups = groups.filter(group => {
    const matchesSearch = !searchTerm || 
      group.group_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.group_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.industry_focus?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = selectedFilter === 'all' || 
      group.group_type === selectedFilter ||
      (selectedFilter === 'high-risk' && group.risk_threshold === 'high') ||
      (selectedFilter === 'active' && group.company_count > 0);

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'var(--ra-deep-blue)',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh'
        }}>
          <div style={{
            backgroundColor: 'var(--ra-glass-bg)',
            borderRadius: '16px',
            padding: '3rem',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <Activity aria-hidden="true" size={48} className="animate-spin" style={{ margin: '0 auto', color: 'var(--ra-cyber-aqua)' }} />
            <h2 style={{ marginTop: '1rem', color: 'var(--ra-text-primary)' }}>Loading Portfolio Groups...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'var(--ra-deep-blue)',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh'
        }}>
          <div style={{
            backgroundColor: 'var(--ra-glass-bg)',
            borderRadius: '16px',
            padding: '3rem',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            borderLeft: '4px solid var(--ra-danger)'
          }}>
            <AlertTriangle aria-hidden="true" size={48} style={{ margin: '0 auto', color: 'var(--ra-danger)' }} />
            <h2 style={{ marginTop: '1rem', color: 'var(--ra-text-primary)' }}>Error Loading Portfolio Groups</h2>
            <p style={{ color: 'var(--ra-danger)', marginBottom: '1rem' }}>{error}</p>
            <button 
              onClick={fetchPortfolioGroups}
              style={{ 
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'var(--ra-deep-blue)'
    }}>
      {/* Header */}
      <div style={{
        background: 'var(--ra-glass-bg)',
        borderBottom: '1px solid var(--ra-glass-border)',
        padding: '2rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div>
              <h1 style={{ 
                margin: '0 0 0.5rem 0', 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                color: 'var(--ra-text-primary)',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                Portfolio Risk Groups
              </h1>
              <p style={{ margin: 0, color: 'var(--ra-text-secondary)', fontSize: '1.1rem' }}>
                Organize and manage client risk pools for better oversight and analysis
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button 
                onClick={() => setShowCreateModal(true)}
                style={{ 
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--ra-cyber-aqua)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <Plus aria-hidden="true" size={18} />
                Create Group
              </button>
              <button 
                onClick={() => navigate('/portfolio-dashboard')}
                style={{ 
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.3s'
                }}
              >
                ← Back to Dashboard
              </button>
            </div>
          </div>

          {/* Search and Filter */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search aria-hidden="true" size={20} style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: 'rgba(255,255,255,0.7)' 
              }} />
              <input
                type="text"
                placeholder="Search groups by name, description, or industry..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search groups by name, description, or industry"
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 3rem',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '1rem'
                }}
              />
            </div>
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              aria-label="Filter groups by type"
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Groups</option>
              <option value="portfolio">Portfolio</option>
              <option value="captive">Insurance Captive</option>
              <option value="mssp">MSSP</option>
              <option value="custom">Custom</option>
              <option value="high-risk">High Risk</option>
              <option value="active">Active Groups</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '2rem auto', padding: '0 2rem' }}>
        
        {/* Summary Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: 'var(--ra-glass-bg)',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--ra-glass-border)',
            borderTop: '4px solid var(--ra-cyber-aqua)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', color: 'var(--ra-text-secondary)', fontSize: '0.875rem', fontWeight: '600' }}>
                  TOTAL GROUPS
                </p>
                <p style={{ margin: 0, fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--ra-text-primary)' }}>
                  {groups.length}
                </p>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users aria-hidden="true" size={24} color="white" />
              </div>
            </div>
          </div>

          <div style={{
            background: 'var(--ra-glass-bg)',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--ra-glass-border)',
            borderTop: '4px solid var(--ra-success)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', color: 'var(--ra-text-secondary)', fontSize: '0.875rem', fontWeight: '600' }}>
                  ACTIVE GROUPS
                </p>
                <p style={{ margin: 0, fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--ra-text-primary)' }}>
                  {groups.filter(g => g.company_count > 0).length}
                </p>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, var(--ra-success) 0%, var(--ra-success) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Activity aria-hidden="true" size={24} color="white" />
              </div>
            </div>
          </div>

          <div style={{
            background: 'var(--ra-glass-bg)',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--ra-glass-border)',
            borderTop: '4px solid var(--ra-cyber-aqua)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', color: 'var(--ra-text-secondary)', fontSize: '0.875rem', fontWeight: '600' }}>
                  TOTAL COMPANIES
                </p>
                <p style={{ margin: 0, fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--ra-text-primary)' }}>
                  {groups.reduce((sum, g) => sum + (g.company_count || 0), 0)}
                </p>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Building aria-hidden="true" size={24} color="white" />
              </div>
            </div>
          </div>

          <div style={{
            background: 'var(--ra-glass-bg)',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--ra-glass-border)',
            borderTop: '4px solid var(--ra-warning)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', color: 'var(--ra-text-secondary)', fontSize: '0.875rem', fontWeight: '600' }}>
                  HIGH RISK GROUPS
                </p>
                <p style={{ margin: 0, fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--ra-text-primary)' }}>
                  {groups.filter(g => g.risk_threshold === 'high').length}
                </p>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, var(--ra-warning) 0%, var(--ra-warning) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertTriangle aria-hidden="true" size={24} color="white" />
              </div>
            </div>
          </div>
        </div>

        {/* Groups Grid */}
        {filteredGroups.length === 0 ? (
          <div style={{
            background: 'var(--ra-glass-bg)',
            borderRadius: '12px',
            padding: '3rem',
            textAlign: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--ra-glass-border)'
          }}>
            {groups.length === 0 ? (
              <>
                <Users aria-hidden="true" size={64} style={{ margin: '0 auto', color: 'var(--ra-text-muted)', marginBottom: '1rem' }} />
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', color: 'var(--ra-text-primary)' }}>
                  No Portfolio Groups Yet
                </h3>
                <p style={{ margin: '0 0 2rem 0', color: 'var(--ra-text-secondary)', fontSize: '1.1rem' }}>
                  Create your first portfolio group to organize clients into risk pools for better management and analysis.
                </p>
                <button 
                  onClick={() => setShowCreateModal(true)}
                  style={{ 
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Plus aria-hidden="true" size={18} />
                  Create First Group
                </button>
              </>
            ) : (
              <>
                <Search aria-hidden="true" size={64} style={{ margin: '0 auto', color: 'var(--ra-text-muted)', marginBottom: '1rem' }} />
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', color: 'var(--ra-text-primary)' }}>
                  No Groups Match Your Search
                </h3>
                <p style={{ margin: '0 0 2rem 0', color: 'var(--ra-text-secondary)', fontSize: '1.1rem' }}>
                  Try adjusting your search terms or filters to find the groups you're looking for.
                </p>
                <button 
                  onClick={() => { setSearchTerm(''); setSelectedFilter('all'); }}
                  style={{ 
                    padding: '0.5rem 1rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid var(--ra-glass-border)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: 'var(--ra-text-primary)'
                  }}
                >
                  Clear Filters
                </button>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '1.5rem' }}>
            {filteredGroups.map((group) => {
              const TypeIcon = getGroupTypeIcon(group.group_type);
              return (
                <div 
                  key={group.group_id}
                  style={{
                    background: 'var(--ra-glass-bg)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    border: '1px solid var(--ra-glass-border)'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  }}
                  onClick={() => handleManageGroup(group)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: `linear-gradient(135deg, ${getGroupTypeColor(group.group_type).includes('blue') ? 'var(--ra-cyber-aqua)' :
                                                                  getGroupTypeColor(group.group_type).includes('green') ? 'var(--ra-success)' :
                                                                  getGroupTypeColor(group.group_type).includes('purple') ? '#8b5cf6' :
                                                                  'var(--ra-warning)'} 0%, ${getGroupTypeColor(group.group_type).includes('blue') ? '#0066ff' :
                                                                                   getGroupTypeColor(group.group_type).includes('green') ? 'var(--ra-success)' :
                                                                                   getGroupTypeColor(group.group_type).includes('purple') ? '#7c3aed' :
                                                                                   'var(--ra-warning)'} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <TypeIcon size={20} color="white" />
                      </div>
                      <div>
                        <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.125rem', fontWeight: '600', color: 'var(--ra-text-primary)' }}>
                          {group.group_name}
                        </h3>
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          textTransform: 'uppercase',
                          backgroundColor: group.group_type === 'captive' ? 'rgba(0, 255, 136, 0.08)' :
                                          group.group_type === 'mssp' ? 'rgba(139, 92, 246, 0.12)' :
                                          group.group_type === 'custom' ? 'rgba(255, 165, 0, 0.08)' : 'rgba(0, 102, 255, 0.08)',
                          color: group.group_type === 'captive' ? 'var(--ra-success)' :
                                 group.group_type === 'mssp' ? '#8b5cf6' :
                                 group.group_type === 'custom' ? 'var(--ra-warning)' : 'var(--ra-cyber-aqua)'
                        }}>
                          {group.group_type}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        textTransform: 'uppercase',
                        ...getRiskThresholdStyle(group.risk_threshold)
                      }}>
                        Target: {group.risk_threshold}
                      </span>
                      {group.calculated_risk_score && (
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          ...getCalculatedRiskStyle(group.calculated_risk_score)
                        }}>
                          Actual: {group.calculated_risk_score}
                        </span>
                      )}
                    </div>
                  </div>

                  <p style={{ 
                    margin: '0 0 1rem 0', 
                    color: 'var(--ra-text-secondary)',
                    fontSize: '0.875rem',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {group.group_description || 'No description provided'}
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ margin: '0 0 0.25rem 0', fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--ra-text-primary)' }}>
                          {group.company_count || 0}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--ra-text-secondary)', textTransform: 'uppercase', fontWeight: '500' }}>
                          Companies
                        </p>
                      </div>
                      {group.unread_alerts > 0 && (
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ margin: '0 0 0.25rem 0', fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--ra-danger)' }}>
                            {group.unread_alerts}
                          </p>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--ra-text-secondary)', textTransform: 'uppercase', fontWeight: '500' }}>
                            Alerts
                          </p>
                        </div>
                      )}
                    </div>
                    {group.industry_focus && (
                      <span style={{ 
                        fontSize: '0.75rem',
                        color: 'var(--ra-text-secondary)',
                        backgroundColor: 'var(--ra-deep-blue)',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontWeight: '500'
                      }}>
                        {group.industry_focus}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.04)' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--ra-text-secondary)' }}>
                      Created {new Date(group.created_at).toLocaleDateString()}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--ra-cyber-aqua)', fontWeight: '500', fontSize: '0.875rem' }}>
                      <span>Manage</span>
                      <ChevronRight aria-hidden="true" size={16} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Group Management Modal */}
      {showManageModal && selectedGroup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--ra-glass-bg)',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '800px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid var(--ra-glass-border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--ra-text-primary)' }}>
                  Manage {selectedGroup.group_name}
                </h2>
                <p style={{ margin: 0, color: 'var(--ra-text-secondary)', fontSize: '0.875rem' }}>
                  Add or remove companies from this portfolio group
                </p>
              </div>
              <button
                onClick={() => {
                  setShowManageModal(false);
                  setSelectedGroup(null);
                  setGroupDetails(null);
                  setSelectedCompanies([]);
                }}
                style={{
                  padding: '0.5rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: 'var(--ra-text-secondary)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.04)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                ✕
              </button>
            </div>

            {groupDetails ? (
              <div>
                {/* Current Companies */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: 'var(--ra-text-primary)' }}>
                    Current Companies ({groupDetails.companies?.length || 0})
                  </h3>
                  {groupDetails.companies && groupDetails.companies.length > 0 ? (
                    <div style={{ display: 'grid', gap: '0.75rem', maxHeight: '200px', overflowY: 'auto', padding: '1rem', backgroundColor: 'var(--ra-deep-blue)', borderRadius: '8px', border: '1px solid var(--ra-glass-border)' }}>
                      {groupDetails.companies.map((company) => (
                        <div key={company.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          borderRadius: '8px',
                          border: '1px solid var(--ra-glass-border)'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Building aria-hidden="true" size={16} color="white" />
                            </div>
                            <div>
                              <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: 'var(--ra-text-primary)' }}>
                                {company.company_name}
                              </p>
                              {company.industry && (
                                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--ra-text-secondary)' }}>
                                  {company.industry}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => removeCompanyFromGroup(company.id)}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: 'rgba(255, 68, 68, 0.08)',
                              color: 'var(--ra-danger)',
                              border: '1px solid rgba(255, 68, 68, 0.2)',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 68, 68, 0.15)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 68, 68, 0.08)';
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '2rem',
                      textAlign: 'center',
                      backgroundColor: 'var(--ra-deep-blue)',
                      borderRadius: '8px',
                      border: '1px solid var(--ra-glass-border)'
                    }}>
                      <Building aria-hidden="true" size={40} style={{ margin: '0 auto', color: 'var(--ra-text-muted)', marginBottom: '0.5rem' }} />
                      <p style={{ margin: 0, color: 'var(--ra-text-secondary)', fontSize: '0.875rem' }}>
                        No companies assigned to this group yet
                      </p>
                    </div>
                  )}
                </div>

                {/* Add Companies */}
                <div>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: 'var(--ra-text-primary)' }}>
                    Add Companies
                  </h3>
                  {availableCompanies && availableCompanies.length > 0 ? (
                    <div>
                      <div style={{ 
                        maxHeight: '250px', 
                        overflowY: 'auto', 
                        border: '1px solid var(--ra-glass-border)',
                        borderRadius: '8px',
                        backgroundColor: 'var(--ra-deep-blue)',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}>
                        {availableCompanies
                          .filter(company => !groupDetails.companies?.find(gc => gc.id === company.id))
                          .map((company) => (
                          <label key={company.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.75rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            borderRadius: '8px',
                            border: '1px solid var(--ra-glass-border)',
                            marginBottom: '0.5rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={e => {
                            if (!selectedCompanies.includes(company.id)) {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                            }
                          }}
                          onMouseLeave={e => {
                            if (!selectedCompanies.includes(company.id)) {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.04)';
                            }
                          }}
                          >
                            <input
                              type="checkbox"
                              aria-label={`Select ${company.company_name || company.name || 'company'}`}
                              checked={selectedCompanies.includes(company.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCompanies([...selectedCompanies, company.id]);
                                } else {
                                  setSelectedCompanies(selectedCompanies.filter(id => id !== company.id));
                                }
                              }}
                              style={{
                                marginRight: '0.75rem',
                                width: '16px',
                                height: '16px',
                                accentColor: 'var(--ra-cyber-aqua)'
                              }}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                              <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                background: 'linear-gradient(135deg, var(--ra-success) 0%, var(--ra-success) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Building aria-hidden="true" size={16} color="white" />
                              </div>
                              <div>
                                <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: 'var(--ra-text-primary)' }}>
                                  {company.company_name}
                                </p>
                                {company.industry && (
                                  <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--ra-text-secondary)' }}>
                                    {company.industry}
                                  </p>
                                )}
                              </div>
                            </div>
                            {company.risk_score && (
                              <div style={{ 
                                padding: '2px 8px', 
                                borderRadius: '12px', 
                                fontSize: '0.75rem', 
                                fontWeight: '500',
                                backgroundColor: company.risk_score >= 80 ? 'rgba(255, 68, 68, 0.08)' :
                                               company.risk_score >= 60 ? 'rgba(255, 165, 0, 0.08)' : 'rgba(0, 255, 136, 0.08)',
                                color: company.risk_score >= 80 ? 'var(--ra-danger)' :
                                       company.risk_score >= 60 ? 'var(--ra-warning)' : 'var(--ra-success)'
                              }}>
                                Risk: {company.risk_score}
                              </div>
                            )}
                          </label>
                        ))}
                        {availableCompanies.filter(company => !groupDetails.companies?.find(gc => gc.id === company.id)).length === 0 && (
                          <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <Users aria-hidden="true" size={40} style={{ margin: '0 auto', color: 'var(--ra-text-muted)', marginBottom: '0.5rem' }} />
                            <p style={{ margin: 0, color: 'var(--ra-text-secondary)', fontSize: '0.875rem' }}>
                              All available companies are already in this group
                            </p>
                          </div>
                        )}
                      </div>

                      {selectedCompanies.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.875rem', color: 'var(--ra-text-secondary)' }}>
                            {selectedCompanies.length} company{selectedCompanies.length !== 1 ? 'ies' : ''} selected
                          </span>
                          <button
                            onClick={addCompaniesToGroup}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            <Plus aria-hidden="true" size={16} />
                            Add {selectedCompanies.length} Company{selectedCompanies.length !== 1 ? 'ies' : ''}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '2rem',
                      textAlign: 'center',
                      backgroundColor: 'var(--ra-deep-blue)',
                      borderRadius: '8px',
                      border: '1px solid var(--ra-glass-border)'
                    }}>
                      <Users aria-hidden="true" size={40} style={{ margin: '0 auto', color: 'var(--ra-text-muted)', marginBottom: '0.5rem' }} />
                      <p style={{ margin: 0, color: 'var(--ra-text-secondary)', fontSize: '0.875rem' }}>
                        No companies available to add
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Activity aria-hidden="true" size={40} className="animate-spin" style={{ margin: '0 auto', color: 'var(--ra-cyber-aqua)', marginBottom: '1rem' }} />
                <p style={{ margin: 0, color: 'var(--ra-text-secondary)' }}>Loading group details...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--ra-glass-bg)',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid var(--ra-glass-border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--ra-text-primary)' }}>
                Create New Portfolio Group
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  padding: '0.5rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: 'var(--ra-text-secondary)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.04)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateGroup}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#ccd6f6', marginBottom: '0.5rem' }}>
                  Group Name *
                </label>
                <input
                  type="text"
                  value={createFormData.name}
                  onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                  aria-label="Group Name"
                  placeholder="e.g., High-Risk Technology Portfolio"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--ra-glass-border)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = 'var(--ra-cyber-aqua)'}
                  onBlur={e => e.target.style.borderColor = 'var(--ra-glass-border)'}
                  required
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#ccd6f6', marginBottom: '0.5rem' }}>
                  Description
                </label>
                <textarea
                  value={createFormData.description}
                  onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                  aria-label="Description"
                  placeholder="Describe the purpose and scope of this group..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--ra-glass-border)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    resize: 'vertical'
                  }}
                  onFocus={e => e.target.style.borderColor = 'var(--ra-cyber-aqua)'}
                  onBlur={e => e.target.style.borderColor = 'var(--ra-glass-border)'}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#ccd6f6', marginBottom: '0.5rem' }}>
                    Group Type
                  </label>
                  <select
                    value={createFormData.groupType}
                    onChange={(e) => setCreateFormData({ ...createFormData, groupType: e.target.value })}
                    aria-label="Group Type"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--ra-glass-border)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      backgroundColor: 'var(--ra-deep-blue)',
                      color: 'var(--ra-text-primary)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="portfolio">Portfolio</option>
                    <option value="captive">Insurance Captive</option>
                    <option value="mssp">MSSP Portfolio</option>
                    <option value="custom">Custom Group</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#ccd6f6', marginBottom: '0.5rem' }}>
                    Risk Threshold
                  </label>
                  <select
                    value={createFormData.riskThreshold}
                    onChange={(e) => setCreateFormData({ ...createFormData, riskThreshold: e.target.value })}
                    aria-label="Risk Threshold"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--ra-glass-border)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      backgroundColor: 'var(--ra-deep-blue)',
                      color: 'var(--ra-text-primary)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="low">Low Risk</option>
                    <option value="medium">Medium Risk</option>
                    <option value="high">High Risk</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#ccd6f6', marginBottom: '0.5rem' }}>
                    Industry Focus (Optional)
                  </label>
                  <input
                    type="text"
                    value={createFormData.industryFocus}
                    onChange={(e) => setCreateFormData({ ...createFormData, industryFocus: e.target.value })}
                    aria-label="Industry Focus"
                    placeholder="e.g., Technology, Healthcare, Finance"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--ra-glass-border)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={e => e.target.style.borderColor = 'var(--ra-cyber-aqua)'}
                    onBlur={e => e.target.style.borderColor = 'var(--ra-glass-border)'}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#ccd6f6', marginBottom: '0.5rem' }}>
                    Max Companies
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={createFormData.maxCompanies}
                    onChange={(e) => setCreateFormData({ ...createFormData, maxCompanies: e.target.value })}
                    aria-label="Max Companies"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--ra-glass-border)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={e => e.target.style.borderColor = 'var(--ra-cyber-aqua)'}
                    onBlur={e => e.target.style.borderColor = 'var(--ra-glass-border)'}
                  />
                </div>
              </div>

              <div style={{ 
                padding: '1rem', 
                backgroundColor: 'var(--ra-deep-blue)',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                border: '1px solid var(--ra-glass-border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Shield aria-hidden="true" size={16} color="var(--ra-cyber-aqua)" />
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#ccd6f6' }}>
                    What happens next?
                  </span>
                </div>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.875rem', 
                  color: 'var(--ra-text-secondary)',
                  lineHeight: '1.5'
                }}>
                  After creating your group, you'll be able to add companies from your portfolio and manage their collective risk assessments, set up automated alerts, and track group performance over time.
                </p>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid var(--ra-glass-border)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: '#ccd6f6',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.04)'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <Plus aria-hidden="true" size={18} />
                  Create Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const getRiskThresholdStyle = (threshold) => {
  switch (threshold?.toLowerCase()) {
    case 'high': return { backgroundColor: 'rgba(255, 68, 68, 0.08)', color: 'var(--ra-danger)' };
    case 'medium': return { backgroundColor: 'rgba(255, 165, 0, 0.08)', color: 'var(--ra-warning)' };
    case 'low': return { backgroundColor: 'rgba(0, 255, 136, 0.08)', color: 'var(--ra-success)' };
    default: return { backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#ccd6f6' };
  }
};

const getCalculatedRiskStyle = (score) => {
  if (score >= 80) return { backgroundColor: 'rgba(255, 68, 68, 0.08)', color: 'var(--ra-danger)' };
  if (score >= 60) return { backgroundColor: 'rgba(255, 165, 0, 0.08)', color: 'var(--ra-warning)' };
  if (score >= 40) return { backgroundColor: 'rgba(0, 255, 136, 0.08)', color: 'var(--ra-success)' };
  return { backgroundColor: 'rgba(0, 102, 255, 0.08)', color: 'var(--ra-cyber-aqua)' };
};

export default PortfolioGroupsManager;